<script>
import { formatMinskDate, capitalizeFirst } from '@/scripts/tools.js';

export default {
    name: 'UserPaymentHistory',
    props: {
        transactions: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        hasData() {
            return this.transactions && this.transactions.length > 0;
        },
    },
    methods: {
        formatDate: formatMinskDate,
        capitalizeFirst,
        formatCurrency(amount, currency) {
            const value = (amount / 100).toFixed(2);
            const symbols = { usd: '$', eur: '€', gbp: '£' };
            const symbol =
                symbols[currency?.toLowerCase()] ||
                currency?.toUpperCase() ||
                '$';
            return `${symbol}${value}`;
        },
        getStatusClass(status) {
            const classes = {
                succeeded: 'status_succeeded',
                pending: 'status_pending',
                failed: 'status_failed',
                refunded: 'status_refunded',
            };
            return classes[status] || 'status_default';
        },
    },
};
</script>

<template>
    <div class="payment_history_content">
        <div v-if="isLoading" class="loading_state">
            Loading payment history...
        </div>
        <div v-else-if="!hasData" class="empty_state">
            No payment transactions found
        </div>
        <div v-else class="table_wrapper">
            <table class="payment_table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Provider</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="transaction in transactions"
                        :key="transaction.id"
                    >
                        <td>{{ formatDate(transaction.created_at) }}</td>
                        <td class="amount_cell">
                            {{
                                formatCurrency(
                                    transaction.amount,
                                    transaction.currency
                                )
                            }}
                        </td>
                        <td>
                            <span
                                class="status_badge"
                                :class="getStatusClass(transaction.status)"
                            >
                                {{ capitalizeFirst(transaction.status) }}
                            </span>
                        </td>
                        <td>
                            {{ capitalizeFirst(transaction.payment_method) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
.payment_history_content {
    padding: 5px 0 0 0;
}

.loading_state,
.empty_state {
    padding: 30px;
    text-align: center;
    color: var(--background-text, #ffffff);
    opacity: 0.7;
}

.table_wrapper {
    max-height: 300px;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
}

.payment_table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
}

.payment_table thead {
    position: sticky;
    top: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
}

.payment_table th {
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    color: var(--background-text, #ffffff);
    opacity: 0.8;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    white-space: nowrap;
}

.payment_table td {
    padding: 10px 12px;
    color: var(--background-text, #ffffff);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 14px;
}

.payment_table tbody tr:hover td {
    background: rgba(255, 255, 255, 0.05);
}

.amount_cell {
    font-weight: 600;
    font-family: monospace;
}

.status_badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

.status_succeeded {
    background-color: #d1fae5;
    color: #065f46;
}

.status_pending {
    background-color: #fef3c7;
    color: #92400e;
}

.status_failed {
    background-color: #fee2e2;
    color: #991b1b;
}

.status_refunded {
    background-color: #f3f4f6;
    color: #4b5563;
}

.status_default {
    background-color: #e5e7eb;
    color: #374151;
}
</style>
